/**
 * LOS CONSENTIMIENTOS DEL PACIENTE, Y EL IPAD, EN EL MISMO SITIO
 * (18/9/2026)
 * ---------------------------------------------------------------------------
 * Hasta hoy, para pasarle el iPad a la paciente que tienes delante había que
 * salir de su ficha, entrar en «Consentimientos» del menú lateral —que es la
 * lista de TODA la clínica, miles de filas—, buscarla ahí y marcar los suyos.
 * Buscar a una persona en la lista de todos para mandarle lo que ya estabas
 * mirando en su ficha es el camino largo, y es donde se marca la fila de otra.
 *
 * Así que el envío vive aquí: en la pestaña «Consentimientos» de su ficha,
 * donde ya están los suyos y solo los suyos. Se marcan varios y se mandan de
 * una vez. El lateral se queda de buscador.
 *
 * TRES COSAS QUE ESTA PANTALLA NO SE INVENTA:
 *
 * 1. Si de tres marcados falla uno, no dice «error» ni «enviados 3»: dice
 *    cuál falló y por qué, y los otros dos siguen enviados. El motivo lo
 *    escribe el servidor (`fallidos[].motivo`), no se traduce aquí.
 * 2. Que la tanda sea de un solo paciente lo comprueba el SERVIDOR: se le
 *    manda `patient_id` para que pueda contradecir a la pantalla.
 * 3. La cola se pide a `/api/consent-tablet/queue`, que es de gestión
 *    (`_gestiona_tablet`). A quien el servidor le diga 403 no se le enseña el
 *    bloque —no es un fallo, es que no es suyo—; pero si falla por CUALQUIER
 *    otra cosa, se dice «no se ha podido consultar», nunca «no hay ninguno».
 *    Un error no se enseña como si fuera un dato.
 * 4. «En la tablet» y «firmando desde casa» NO son lo mismo (21/9/2026).
 *    Aquí se pintaba «En la tablet, esperando firma» de todo lo pendiente,
 *    incluido lo que el paciente tiene en el enlace de su WhatsApp y el iPad
 *    no enseña. En el mostrador se creía que ya estaba en la tablet y no se
 *    mandaba: se le pasaba el iPad a la paciente, no salía nada suyo, y el CI
 *    llegaba sin firmar al día de la cirugía. Y encima invitaba a pulsar la X
 *    de esa fila, que anulaba el enlace que ella tenía en el móvil.
 *    Quién está en la tablet lo dice el SERVIDOR (`en_tablet` de /queue);
 *    esta pantalla solo lo pinta.
 * 5. Y LA FIRMA DEL MÉDICO (21/9/2026). El médico que figura en el
 *    consentimiento lo firma aquí mismo, con el dedo, en el mismo recuadro que
 *    usa la hoja de anestesia (<FirmaDedo>). Dos cosas que esta pantalla NO
 *    decide: quién puede firmar cada uno —lo contesta el servidor en
 *    /api/consents/firma-medico y lo vuelve a comprobar al firmar, que a un
 *    botón escondido se le da la vuelta— y si a un papel le falta la firma del
 *    médico: los del titular salen con su firma y su sello guardados y no les
 *    falta nada. Si esa consulta falla no se pinta «falta la firma»: se dice
 *    que no se ha podido comprobar. Un error no se enseña como si fuera un dato.
 *
 * Ejecutar:
 *   cd frontend && CI=true npx craco test --watchAll=false \
 *     src/components/patient/PatientConsentsTab.test.jsx
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { api, fullName, formatDate, formatDateTime, mensajeError } from '../../lib/api';
import { SectionCard, EmptyState } from '../Common';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import FirmaDedo from '../FirmaDedo';
import { CheckCircle2, Eye, FilePen, Tablet, AlertTriangle, X, Clock, Send, Signature } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/** Un consentimiento está firmado por el paciente si tiene firma, y punto. */
function firmado(c) {
  return !!(c.patient_signature_b64 || '').trim();
}

/** Y por el médico, si tiene la suya (la del titular va en el PDF, no aquí). */
function firmadoPorElMedico(c) {
  return !!(c.doctor_signature_b64 || '').trim();
}

export default function PatientConsentsTab({ patient, consents, onPreview, onReload }) {
  const lista = useMemo(() => consents || [], [consents]);
  const [seleccion, setSeleccion] = useState(() => new Set());
  const [enviando, setEnviando] = useState(false);
  // Lo que contestó el último envío: se queda en pantalla hasta el siguiente.
  // Un toast se va solo en 4 segundos y aquí puede haber un fallo que hay que
  // leer con calma (y a veces llamar al paciente).
  const [ultimoEnvio, setUltimoEnvio] = useState(null);
  // `cola` tiene tres estados a propósito: null = aún no se sabe;
  // {items} = lo que hay; {fallo} = no se pudo consultar. Nunca una lista
  // vacía por haber fallado.
  const [cola, setCola] = useState(null);

  const cargarCola = useCallback(async () => {
    if (!patient?.id) return;
    try {
      const r = await api.get('/api/consent-tablet/queue', { params: { patient_id: patient.id } });
      setCola({ items: r.data?.items || [] });
    } catch (e) {
      // 403 = esta pantalla no es para este perfil (la cola la gestiona
      // administración). No es una avería: no se enseña el bloque.
      if (e?.response?.status === 403) setCola({ sinPermiso: true });
      else setCola({ fallo: e?.response?.data?.detail || 'no se pudo consultar' });
    }
  }, [patient?.id]);

  useEffect(() => { cargarCola(); }, [cargarCola]);

  // --- Quién puede firmar cada consentimiento como MÉDICO -------------------
  // Mismos tres estados que la cola, y por el mismo motivo: null = aún no se
  // sabe; {porId} = lo que contestó el servidor; {fallo} = no se pudo
  // consultar. Con {fallo} esta pantalla no dice que a nada le falte la firma
  // del médico, porque no lo sabe.
  const [firmaMedico, setFirmaMedico] = useState(null);
  // El consentimiento que se está firmando ahora mismo (el del diálogo).
  const [firmando, setFirmando] = useState(null);
  const [guardandoFirma, setGuardandoFirma] = useState(false);
  const firmaRef = useRef(null);

  const cargarFirmaMedico = useCallback(async () => {
    if (!patient?.id) return;
    try {
      const r = await api.get('/api/consents/firma-medico', { params: { patient_id: patient.id } });
      const porId = new Map();
      (r.data?.items || []).forEach((x) => { if (x.consent_id) porId.set(x.consent_id, x); });
      setFirmaMedico({ porId });
    } catch (e) {
      setFirmaMedico({ fallo: mensajeError(e, 'no se pudo consultar') });
    }
  }, [patient?.id]);

  useEffect(() => { cargarFirmaMedico(); }, [cargarFirmaMedico]);

  const guardarFirmaMedico = async () => {
    if (!firmando || guardandoFirma) return;
    if (!firmaRef.current || firmaRef.current.isEmpty()) {
      toast.error('Dibuja la firma antes de guardar');
      return;
    }
    setGuardandoFirma(true);
    try {
      const b64 = firmaRef.current.getCanvas().toDataURL('image/png');
      await api.put(`/api/consents/${firmando.id}/firma-medico`, { firma_b64: b64 });
      toast.success('Consentimiento firmado');
      setFirmando(null);
      await cargarFirmaMedico();
      onReload && onReload();
    } catch (e) {
      // El motivo lo escribe el servidor (es quien sabe de quién es el
      // documento): se enseña tal cual, no se traduce aquí.
      toast.error(mensajeError(e, 'No se pudo firmar'));
    } finally {
      setGuardandoFirma(false);
    }
  };

  // La selección se poda sola: fuera lo que ya no está en la ficha o acaba de
  // firmarse (mismo cuidado que tenía la pantalla lateral — si no, el botón
  // manda ids fantasma).
  useEffect(() => {
    setSeleccion((prev) => {
      const vivos = new Set(lista.filter((c) => !firmado(c)).map((c) => c.id));
      const next = new Set([...prev].filter((id) => vivos.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [lista]);

  const pendientes = useMemo(() => lista.filter((c) => !firmado(c)), [lista]);
  // Dónde está esperando firma cada uno: en el IPAD o en el ENLACE de casa.
  // Quién está en el iPad lo calcula el servidor (`en_tablet`); el `??` es
  // solo para el rato del despliegue, mientras el servidor viejo sigue
  // contestando sin ese campo.
  const enCola = useMemo(() => {
    const m = new Map();
    (cola?.items || []).forEach((p) => {
      if (p.status !== 'pending' || !p.consent_id) return;
      const enTablet = p.en_tablet ?? (!!p.tablet || p.via !== 'link');
      m.set(p.consent_id, { ...p, enTablet });
    });
    return m;
  }, [cola]);

  const alternar = (id) => setSeleccion((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const enviar = async () => {
    const ids = Array.from(seleccion);
    if (!ids.length || enviando) return;
    setEnviando(true);
    try {
      const r = await api.post('/api/consent-tablet/send', {
        consent_ids: ids,
        // De quién dice la pantalla que es la tanda. El servidor lo comprueba.
        patient_id: patient.id,
      });
      const d = r.data || {};
      const fallidos = d.fallidos || [];
      const hechos = (d.enviados ?? 0) + (d.reutilizados ?? 0);
      setUltimoEnvio({ hechos, fallidos, otros: d.otros_en_cola || [], cuando: new Date() });
      if (fallidos.length) {
        // Ni «error» a secas ni un «enviados 3» que no es verdad: cuáles y por qué.
        toast.warning(`${hechos} de ${ids.length} en la cola · falló ${fallidos.length}: `
          + fallidos.map((f) => `${f.nombre} (${f.motivo})`).join(' · '));
      } else {
        toast.success(`${hechos} consentimiento${hechos === 1 ? '' : 's'} en la cola de la tablet. `
          + 'Pasa el iPad al paciente: los firmará uno detrás de otro.');
      }
      setSeleccion(new Set());
      await cargarCola();
      onReload && onReload();
    } catch (e) {
      const motivo = e?.response?.data?.detail || 'error desconocido';
      setUltimoEnvio({ hechos: 0, fallidos: [], error: motivo, cuando: new Date() });
      toast.error(`No se pudo enviar a la tablet: ${motivo}`);
    } finally {
      setEnviando(false);
    }
  };

  // Solo se ofrece para lo que está en el iPad, y solo lo saca del iPad. Lo
  // que además esté en el enlace del paciente sigue firmable desde casa: eso
  // lo garantiza el servidor (no cancela un pending de enlace salvo que se le
  // pida a propósito), y aquí se dice, porque quien pulsa tiene que saber qué
  // pasa con el enlace que mandó por WhatsApp.
  const quitarDeLaCola = async (pending) => {
    const deEnlace = pending.via === 'link';
    if (!window.confirm(`¿Quitar «${pending.template_name || 'este consentimiento'}» de la cola de la tablet?`
      + (deEnlace
        ? '\n\nSale del iPad; el enlace de firma que tiene el paciente sigue funcionando.'
        : '\n\nDeja de estar esperando firma; para que vuelva hay que mandarlo otra vez.'))) return;
    try {
      await api.delete(`/api/consent-tablet/${pending.id}`);
      toast.success(deEnlace
        ? 'Quitado de la cola de la tablet (el enlace del paciente sigue activo)'
        : 'Quitado de la cola de la tablet');
      await cargarCola();
    } catch (e) {
      toast.error(`No se pudo quitar de la cola: ${e?.response?.data?.detail || 'error'}`);
    }
  };

  const accion = (
    <div className="flex items-center gap-3">
      <Link to={`/consentimientos?patient=${patient.id}`} className="text-sm text-[#2E6E5E] hover:underline">+ Generar</Link>
    </div>
  );

  return (
    <SectionCard title="Consentimientos" action={accion}>
      {/* --- Barra de envío: lo que vino del menú lateral ------------------ */}
      {pendientes.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="patient-consent-envio-barra">
          <Button
            size="sm"
            onClick={enviar}
            disabled={!seleccion.size || enviando}
            className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
            data-testid="patient-consent-enviar-tablet"
            title="Encola los marcados: el paciente los firma en la tablet uno detrás de otro"
          >
            <Tablet className="w-4 h-4 mr-1" />
            {enviando ? 'Enviando…' : `Enviar a la tablet (${seleccion.size})`}
          </Button>
          {seleccion.size !== pendientes.length && (
            <Button size="sm" variant="outline" onClick={() => setSeleccion(new Set(pendientes.map((c) => c.id)))}
              data-testid="patient-consent-marcar-todos">
              Marcar los {pendientes.length} pendientes
            </Button>
          )}
          {seleccion.size > 0 && (
            <Button size="sm" variant="ghost" onClick={() => setSeleccion(new Set())}>Quitar selección</Button>
          )}
        </div>
      )}

      {/* --- Qué pasó en el último envío ---------------------------------- */}
      {ultimoEnvio && (
        <div className="mb-3 rounded-md border border-border p-3 text-xs space-y-1" data-testid="patient-consent-envio-resultado">
          {ultimoEnvio.error ? (
            <div className="text-red-700 font-medium" data-testid="patient-consent-envio-error">
              No se envió nada: {ultimoEnvio.error}
            </div>
          ) : (
            <div className={ultimoEnvio.fallidos.length ? 'text-amber-700 font-medium' : 'text-emerald-700 font-medium'}>
              {ultimoEnvio.hechos} en la cola de la tablet
              {ultimoEnvio.fallidos.length ? ` · ${ultimoEnvio.fallidos.length} sin enviar` : ''}
            </div>
          )}
          {ultimoEnvio.fallidos.map((f) => (
            <div key={f.consent_id} className="text-red-700" data-testid="patient-consent-envio-fallido">
              ▸ {f.nombre}: {f.motivo}
            </div>
          ))}
          {(ultimoEnvio.otros || []).length > 0 && (
            <div className="text-amber-700 flex items-start gap-1" data-testid="patient-consent-otros-en-cola">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                Ojo: en la cola de la tablet hay consentimientos de otro paciente
                {' '}({(ultimoEnvio.otros || []).map((o) => `${o.patient_name} · ${o.cuantos}`).join(' · ')}).
                {' '}La tablet enseña primero el más antiguo, así que saldrán antes que estos.
              </span>
            </div>
          )}
        </div>
      )}

      {/* --- La lista de siempre, ahora con casilla ----------------------- */}
      {lista.length === 0 ? (
        <EmptyState icon={FilePen} title="Sin consentimientos" description="Genera el consentimiento para la cirugía." />
      ) : (
        <div className="divide-y divide-border">
          {lista.map((c) => {
            const pend = enCola.get(c.id);
            // Lo que el servidor dice de la firma del MÉDICO de este papel.
            // `undefined` = todavía no se sabe (o no se pudo preguntar): no se
            // pinta nada al respecto, que es distinto de «no le falta».
            const quienFirma = firmaMedico?.porId?.get(c.id);
            return (
              <div key={c.id} className="py-3 flex items-center justify-between gap-3" data-testid="patient-consent-row">
                <div className="flex items-start gap-2.5 min-w-0">
                  {!firmado(c) ? (
                    <input
                      type="checkbox"
                      className="w-4 h-4 accent-[#2E6E5E] cursor-pointer mt-0.5 shrink-0"
                      checked={seleccion.has(c.id)}
                      onChange={() => alternar(c.id)}
                      title="Marcar para enviarlo a la tablet junto con los demás"
                      data-testid={`patient-consent-check-${c.id}`}
                    />
                  ) : <span className="w-4 shrink-0" />}
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{c.template_name}{c.surgery_name ? ` · ${c.surgery_name}` : ''}</div>
                    <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      <span>{formatDate(c.created_at)}</span>
                      {/* Firmas: la del paciente llega desde la tablet, el enlace de
                          firma o su guía «mis pasos» (2026-09-03); la del anestesista,
                          desde Quirófano. Aquí se ve sin abrir el PDF. */}
                      {firmado(c) ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 font-medium" data-testid="patient-consent-firmado">
                          <CheckCircle2 className="w-3 h-3" /> Firmado por el paciente{c.patient_signed_at ? ` · ${formatDate(c.patient_signed_at)}` : ''}
                        </span>
                      ) : pend?.enTablet ? (
                        <span className="inline-flex items-center gap-1 text-[#2E6E5E] font-medium" data-testid="patient-consent-en-cola">
                          <Clock className="w-3 h-3" /> En la tablet, esperando firma{pend.queued_at ? ` · desde ${formatDateTime(pend.queued_at)}` : ''}
                        </span>
                      ) : pend ? (
                        <span className="inline-flex items-center gap-1 text-[#2E6E5E] font-medium" data-testid="patient-consent-en-enlace">
                          <Send className="w-3 h-3" /> Enviado a firmar desde casa, aún sin firmar{pend.queued_at ? ` · desde ${formatDateTime(pend.queued_at)}` : ''}
                        </span>
                      ) : (
                        // «Pendiente de firma» a secas se leía mal desde que hay
                        // dos firmas en juego (21/9/2026): se dice de quién falta.
                        <span className="text-amber-700" data-testid="patient-consent-pendiente">Falta la firma del paciente</span>
                      )}
                      {/* La del MÉDICO (21/9/2026). Que FALTE solo se dice si el
                          servidor ha contestado que este papel la espera: los del
                          titular salen con su firma y su sello y no les falta nada. */}
                      {firmadoPorElMedico(c) ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 font-medium"
                          data-testid={`patient-consent-firmado-medico-${c.id}`}>
                          <CheckCircle2 className="w-3 h-3" /> Firmado por el médico
                          {c.doctor_signature_name ? ` ${c.doctor_signature_name}` : ''}
                          {c.doctor_signature_license ? ` · Colegiado ${c.doctor_signature_license}` : ''}
                          {c.doctor_signed_at ? ` · ${formatDate(c.doctor_signed_at)}` : ''}
                        </span>
                      ) : quienFirma?.necesita_firma ? (
                        <span className="text-amber-700" data-testid={`patient-consent-falta-medico-${c.id}`}>
                          Falta la firma del médico{quienFirma.medico ? ` (${quienFirma.medico})` : ''}
                        </span>
                      ) : null}
                      {(c.anesthetist_signature_b64 || '').trim() ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 font-medium" data-testid="patient-consent-anestesista">
                          <CheckCircle2 className="w-3 h-3" /> Firmado por el anestesista
                          {c.anesthetist_name ? ` ${c.anesthetist_name}${c.anesthetist_license ? ` · Colegiado ${c.anesthetist_license}` : ''}` : ''}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {/* Firmar: solo a quien el SERVIDOR dice que es el médico de
                      ESE documento. Y si ya firmó, «Firmar de nuevo» —la
                      anterior se guarda, no se pisa—. */}
                  {quienFirma?.puede_firmar && (
                    <Button size="sm" variant={firmadoPorElMedico(c) ? 'ghost' : 'outline'}
                      onClick={() => setFirmando(c)}
                      className={firmadoPorElMedico(c) ? 'text-muted-foreground' : 'text-[#2E6E5E] border-[#2E6E5E]'}
                      title={firmadoPorElMedico(c)
                        ? 'Volver a firmarlo (la firma anterior se guarda)'
                        : 'Firmar este consentimiento como médico'}
                      data-testid={`patient-consent-firmar-medico-${c.id}`}>
                      <Signature className="w-4 h-4 mr-1" />
                      {firmadoPorElMedico(c) ? 'Firmar de nuevo' : 'Firmar'}
                    </Button>
                  )}
                  {pend?.enTablet && (
                    <Button size="sm" variant="ghost" onClick={() => quitarDeLaCola(pend)}
                      className="text-muted-foreground" title="Quitarlo de la cola de la tablet"
                      data-testid={`patient-consent-quitar-cola-${c.id}`}>
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onPreview(
                      `${BACKEND_URL}/api/consents/${c.id}/pdf`,
                      `consentimiento_${c.template_code}_${patient.nif || patient.id}.pdf`,
                      `Consentimiento · ${c.template_name} · ${fullName(patient)}`,
                    )}
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* --- Si la cola no se pudo consultar, se dice ---------------------- */}
      {cola?.fallo && (
        <div className="mt-3 text-xs text-red-700 flex items-center gap-1" data-testid="patient-consent-cola-fallo">
          <AlertTriangle className="w-3.5 h-3.5" />
          No se ha podido consultar la cola de la tablet ({cola.fallo}): no se puede decir cuáles están ya esperando firma en el iPad.
        </div>
      )}

      {/* --- Y si no se pudo saber quién firma, TAMPOCO se calla ----------- */}
      {firmaMedico?.fallo && (
        <div className="mt-3 text-xs text-red-700 flex items-center gap-1" data-testid="patient-consent-firma-medico-fallo">
          <AlertTriangle className="w-3.5 h-3.5" />
          No se ha podido comprobar quién firma estos consentimientos ({firmaMedico.fallo}): no se puede decir a cuáles les falta la firma del médico.
        </div>
      )}

      {/* --- El recuadro de firma del médico ------------------------------- */}
      {/* El MISMO <FirmaDedo> de la hoja de anestesia y de las páginas de
          firma del paciente: no hay un segundo sistema de firmas en la casa. */}
      <Dialog open={!!firmando} onOpenChange={(abierto) => { if (!abierto) setFirmando(null); }}>
        <DialogContent className="max-w-lg" data-testid="firma-medico-dialogo">
          <DialogHeader>
            <DialogTitle>Firmar «{firmando?.template_name}»</DialogTitle>
          </DialogHeader>
          {firmando && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                {fullName(patient)}
                {firmaMedico?.porId?.get(firmando.id)?.medico
                  ? ` · como ${firmaMedico.porId.get(firmando.id).medico}`
                  : ''}
                {firmaMedico?.porId?.get(firmando.id)?.colegiado
                  ? ` · Colegiado ${firmaMedico.porId.get(firmando.id).colegiado}`
                  : ''}
              </p>
              {firmadoPorElMedico(firmando) && (
                <p className="text-xs text-amber-700" data-testid="firma-medico-ya-firmado">
                  Este consentimiento ya está firmado: la firma anterior se guarda como repetición, no se borra.
                </p>
              )}
              <FirmaDedo ref={firmaRef} testid="firma-medico" alturaCls="h-40" penColor="#13231E" />
              <div className="flex items-center gap-2">
                <Button size="sm" className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
                  disabled={guardandoFirma} onClick={guardarFirmaMedico}
                  data-testid="firma-medico-guardar">
                  {guardandoFirma ? 'Guardando…' : 'Guardar firma'}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setFirmando(null)}>Cancelar</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
}
